import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiCalendar, FiClock, FiCheckCircle, FiXCircle,
  FiTruck, FiPlus, FiChevronRight
} from 'react-icons/fi';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import { getImageUrl, getPlaceholderUrl } from '../../utils/imageUrl';

const CustomerDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch recent bookings and featured services
        const [bookingsRes, servicesRes] = await Promise.all([
          API.get('/api/booking/customer/my-bookings', {
            params: { page: 1, pageSize: 3 }
          }),
          API.get('/api/service', {
            params: {
              featured: true,
              limit: 3
            }
          })
        ]);

        // Check if the response has the new pagination format
        const bookingsData = bookingsRes.data?.bookings || bookingsRes.data || [];

        // Format booking data
        const formattedBookings = bookingsData.slice(0, 3).map(booking => {
          // Determine mechanic name
          let mechanicName = 'Belum ditugaskan';
          if (booking.mechanic_id && booking.mechanic_name) {
            mechanicName = booking.mechanic_name;
          }

          return {
            id: booking.id,
            service: booking.service_name,
            date: booking.booking_date || booking.date || 'Tanggal tidak tersedia',
            time: booking.booking_time || booking.time || 'Waktu tidak tersedia',
            status: booking.status,
            mechanicName: mechanicName,
            vehicle: `${booking.make || ''} ${booking.model || ''} ${booking.year ? `(${booking.year})` : ''}`.trim()
          };
        });

        setBookings(formattedBookings);
        setServices(servicesRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setBookings([]);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">
            Menunggu Konfirmasi
          </span>
        );
      case 'confirmed':
        return (
          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
            Terjadwal
          </span>
        );
      case 'in-progress':
        return (
          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold">
            Sedang Dikerjakan
          </span>
        );
      case 'completed':
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
            Selesai
          </span>
        );
      case 'canceled':
      case 'cancelled':
        return (
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold">
            Dibatalkan
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-semibold">
            Status Tidak Diketahui
          </span>
        );
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FiClock className="text-yellow-500" />;
      case 'confirmed':
        return <FiCalendar className="text-blue-500" />;
      case 'in-progress':
        return <FiTruck className="text-purple-500" />;
      case 'completed':
        return <FiCheckCircle className="text-green-500" />;
      case 'canceled':
      case 'cancelled':
        return <FiXCircle className="text-red-500" />;
      default:
        return <FiClock className="text-gray-500" />;
    }
  };

  const formatPrice = (price) => {
    if (!price || isNaN(price)) {
      return 'Rp-';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" userName="Pelanggan">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="customer" userName="Pelanggan">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-secondary">Dashboard Pelanggan</h1>
        <p className="text-gray-600">Selamat datang di XSignature Auto Garage</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-8">
        <Link
          to="/customer/book"
          className="bg-primary text-white rounded-lg p-6 flex items-center hover:bg-primary-dark transition-colors"
        >
          <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
            <FiPlus size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Booking Baru</h3>
            <p className="text-sm text-white text-opacity-80">Buat booking servis baru</p>
          </div>
        </Link>

        <Link
          to="/customer/bookings"
          className="bg-secondary text-white rounded-lg p-6 flex items-center hover:bg-secondary-light transition-colors"
        >
          <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
            <FiCalendar size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Booking Saya</h3>
            <p className="text-sm text-white text-opacity-80">Lihat semua booking Anda</p>
          </div>
        </Link>
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-secondary">Booking Terbaru</h2>
          <Link to="/customer/bookings" className="text-primary hover:text-primary-dark flex items-center text-sm">
            Lihat Semua <FiChevronRight className="ml-1" />
          </Link>
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-8">
            <div className="bg-gray-100 inline-block p-3 rounded-full mb-3">
              <FiCalendar className="text-gray-500" size={24} />
            </div>
            <p className="text-gray-500 mb-4">Anda belum memiliki booking servis</p>
            <Link
              to="/customer/book"
              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition-colors inline-flex items-center"
            >
              <FiPlus className="mr-2" /> Buat Booking Baru
            </Link>
          </div>
        ) : (
          <div className="relative">
            <div className="overflow-x-auto">
              {/* Optional message when table is scrollable */}
              <div className="text-xs text-gray-500 mb-1 text-right italic md:hidden">
                <span>← Geser untuk melihat selengkapnya →</span>
              </div>
              <div className="shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Layanan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tanggal & Waktu</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kendaraan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Montir</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{booking.service}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{booking.date}</div>
                          <div className="text-sm text-gray-500">{booking.time}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="mr-2">
                              {getStatusIcon(booking.status)}
                            </div>
                            {getStatusBadge(booking.status)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {booking.vehicle}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-primary">
                          {booking.mechanicName}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Featured Services */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-secondary">Layanan Unggulan</h2>
          <Link to="/services" className="text-primary hover:text-primary-dark flex items-center text-sm">
            Lihat Semua <FiChevronRight className="ml-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service) => {
            const imageUrl = service.image_url || service.image;
            const fullImageUrl = imageUrl ?
              (imageUrl.startsWith('http') ? imageUrl : getImageUrl(imageUrl)) :
              getPlaceholderUrl('No+Image');

            return (
              <div key={service.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-40 overflow-hidden">
                  <img
                    src={fullImageUrl}
                    alt={service.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getPlaceholderUrl('No+Image');
                    }}
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{service.description}</p>
                  <div className="flex justify-between items-center">
                    <div className="font-medium text-primary">{formatPrice(service.price)}</div>
                    <div className="flex items-center text-sm text-gray-500">
                      <FiClock className="mr-1" />
                      <span>{service.estimated_time} menit</span>
                    </div>
                  </div>
                </div>
                <div className="border-t p-4">
                  <Link
                    to={`/customer/book?service=${service.id}`}
                    className="w-full bg-primary text-white py-2 rounded flex items-center justify-center hover:bg-primary-dark transition-colors"
                  >
                    <FiPlus className="mr-2" /> Booking Sekarang
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomerDashboard; 
